import { isEqual } from 'lodash';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import { useTranslation } from 'react-i18next';
import { useWorkflowStore } from '@/state/workflow.state';
import { useGlobalStore } from '@/state/global.state';
import { I18N_KEYS } from '@/language/keys';
import { getNodeOriginalName, stackNodesAndEdgesForRendering } from '../Nodes/utils/nodeUtils';
import { WebGLNotSupportedPage } from '../Errors/ErrorPage/WebGLNotSupportedPage';
import { ErrorPage } from '../Errors/ErrorPage/ErrorPage';
import { GPUDriverErrorPage } from '../Errors/ErrorPage/GPUDriverErrorPage';
import { FlowGraph, FlowErrorType } from './FlowGraph';
import { CompositorView } from './Views/CompositorView';
import { BlurView } from './Views/BlurView';
import { ComparisonView } from './Views/ComparisonView';
import { BlendView } from './Views/BlendView';
import { DilationErosionView } from './Views/DilationErosionView';
import { ChannelsView } from './Views/ChannelsView';
import { FlowView } from './Views/FlowView';
import { ModelBaseView } from './Views/ModelBaseView';
import { LevelsView } from './Views/LevelsView';
import { TextView } from './Views/TextView';
import { PromptView } from './Views/PromptView';
import { NumberView } from './Views/NumberView';
import { ImportView } from './Views/ImportView';
import { ResizeView } from './Views/ResizeView';
import { PromptConcatView } from './Views/PromptConcatView';
import { ListSelectorView } from './Views/ListSelectorView';
import { NumberSelectorView } from './Views/NumberSelectorView';
import { StringArrayView } from './Views/StringArrayView';
import { ExportView } from './Views/ExportView';
import { ToggleView } from './Views/ToggleView';
import { SeedView } from './Views/SeedView';
import { CropView } from './Views/CropView';
import { VideoView } from './Views/VideoView';
import { ParameterView } from './Views/ParameterView/ParameterView';
import { LoraView } from './Views/LoraView';
import { PainterView } from './Views/PainterView';
import { MediaIteratorView } from './Views/MediaIteratorView';
import { CustomGroupView } from './Views/CustomGroupView';
import { GroupsManagerView } from './Views/NodeGrouping/GroupsManagerView';
import { MaskExtractorView } from './Views/MaskExtractorView';
import { ImportModelView } from './Views/ImportModelView';
import { StickyNoteView } from './Views/StickyNoteView';
import { FigmaCompositorView } from './Views/FigmaCompositorView';
import { NodeAssetManager } from './Views/NodeAssetManager';
import { ThreeDView } from './Views/ThreeDView';
import { PointCloudView } from './Views/PointCloudView';
import { PreviewView } from './Views/PreviewView';
import { KlingElementView } from './Views/KlingElementView';
import { useEditorStore } from './FlowComponents/Editor/editor.state';
import type { EvaluatedValue, NodeId, InputId } from 'web';
import type { Node, Edge, BaseNodeData } from '@/types/node';
import type { FlowUIState, FlowWarningType } from './FlowGraph';

let canvasInst: HTMLCanvasElement | null;
function getCanvas() {
  if (canvasInst != null) return canvasInst;
  canvasInst = document.createElement('canvas');
  canvasInst.style.visibility = 'hidden';
  canvasInst.style.position = 'absolute';
  canvasInst.style.top = '100vh';
  canvasInst.style.width = '1024px';
  canvasInst.style.height = '1024px';
  return canvasInst;
}

export function useCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(getCanvas());
  const setParent = useCallback((parent: Element) => {
    const canvas = canvasRef.current;
    parent.appendChild(canvas);
    canvas.style.visibility = 'visible';
    canvas.style.position = '';
    canvas.style.top = '';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
  }, []);

  const unsetParent = useCallback((parent: Element) => {
    const canvas = parent.removeChild(canvasRef.current);
    canvas.style.visibility = 'hidden';
    canvas.style.position = 'absolute';
    canvas.style.top = '100vh';
    canvas.style.width = '1024px';
    canvas.style.height = '1024px';

    canvasRef.current = canvas;
  }, []);

  return { canvasRef, setParent, unsetParent };
}

const FlowContext = createContext<{ graph: FlowGraph } | null>(null);
export const StorybookFlowContext = FlowContext;

function useFlowContext() {
  const ctx = useContext(FlowContext);
  if (!ctx) throw new Error('Missing FlowContext.Provider in the tree');
  return ctx;
}

export function useVideoState(nodeId: NodeId, index: number = 0) {
  return useFlowState((state) => state.video[nodeId]?.[index]);
}

export function useFlowState<T>(selector: (state: FlowUIState) => T): T {
  const { graph } = useFlowContext();
  const snapshot = useRef<T>(undefined);
  const getSnapshot = useCallback(() => {
    const newValue = selector(graph.getUIState());
    if (!isEqual(newValue, snapshot.current)) {
      snapshot.current = newValue;
    }
    return snapshot.current!;
  }, [graph, selector]);

  const subscribe = useCallback((f: () => void) => graph.addUIStateListener(f), [graph]);
  return useSyncExternalStore(subscribe, getSnapshot);
}

/**
 * A *reactive* hook that returns the nodes of the flow graph.
 *
 * @example
 * ```tsx
 * const nodes = useNodes();
 * nodes.filter((node) => node.type === 'blurV2');
 * ```
 *
 * @returns The nodes of the flow graph
 */
export function useNodes(): Node[] {
  const { graph } = useFlowContext();
  const subscribe = useCallback((f: () => void) => graph.store.addNodesListener(f), [graph]);
  return useSyncExternalStore(subscribe, () => graph.store.getNodes());
}

/**
 * A *reactive* hook that returns the nodes and edges stacked for proper z-ordering in rendering.
 * @returns The nodes and edges with z-index properly set for rendering
 */
export function useStackedNodesAndEdgesForRendering(): {
  nodes: (Node & { zIndex?: number })[];
  edges: Edge[];
} {
  const nodes = useNodes();
  const edges = useEdges();
  return useMemo(() => stackNodesAndEdgesForRendering(nodes, edges), [nodes, edges]);
}

export function useNode(id: NodeId): Node {
  const { graph } = useFlowContext();
  const subscribe = useCallback((f: () => void) => graph.store.addNodeListener(id, f), [graph, id]);
  return useSyncExternalStore(subscribe, () => graph.store.getNode(id));
}

export function useNodeNullable(id: NodeId | undefined): Node | undefined {
  const { graph } = useFlowContext();
  return useSyncExternalStore(
    (f) => (id ? graph.store.addNodeListener(id, f) : () => {}),
    () => (id ? graph.store.getNodeNullable(id) : undefined),
  );
}

export function useNodeData<T extends BaseNodeData>(id: NodeId): T {
  const { graph } = useFlowContext();
  const subscribe = useCallback((f: () => void) => graph.store.addNodeListener(id, f), [graph, id]);
  return useSyncExternalStore(subscribe, () => graph.store.getNodeData<T>(id));
}

/**
 * Subscribes to node updates but only snapshots `Node.originalName` (no full node in component state).
 */
export function useNodeOriginalName(nodeId: NodeId | undefined): string | undefined {
  const { graph } = useFlowContext();
  const subscribe = useCallback(
    (f: () => void) => (nodeId ? graph.store.addNodeListener(nodeId, f) : () => {}),
    [graph, nodeId],
  );
  const getSnapshot = useCallback(() => {
    if (!nodeId) return undefined;
    const node = graph.store.getNodeNullable(nodeId);
    if (!node) return undefined;
    return getNodeOriginalName(node);
  }, [graph, nodeId]);
  return useSyncExternalStore(subscribe, getSnapshot);
}

export function useIsNodeInGroup(nodeId: NodeId): boolean {
  const node = useNodeNullable(nodeId);
  return !!node?.parentId;
}

/**
 * A *reactive* hook that returns the effective lock state of a node.
 * This considers parent group lock state - if the node is in a locked group,
 * it returns true even if the node itself is not locked.
 *`
 * @param nodeId The ID of the node to check
 * @returns The effective lock state (true if locked, false otherwise)
 */
export function useEffectiveIsLocked(nodeId: NodeId | undefined): boolean {
  const { graph } = useFlowContext();
  // Track the current parent ID reactively so we re-subscribe when it changes
  const currentParentId = useNodeNullable(nodeId)?.parentId;
  const subscribe = useCallback(
    (f: () => void) => {
      // Subscribe to the node itself
      if (!nodeId) {
        return () => {};
      }
      const unsubscribeNode = graph.store.addNodeListener(nodeId, f);
      // Also subscribe to parent if it exists
      const unsubscribeParent = currentParentId ? graph.store.addNodeListener(currentParentId, f) : () => {};
      // Return combined unsubscribe function
      return () => {
        unsubscribeNode();
        unsubscribeParent();
      };
    },
    [graph, nodeId, currentParentId],
  );
  return useSyncExternalStore(subscribe, () => (nodeId ? graph.getEffectiveIsLocked(nodeId) : false));
}

export function useNodeDataNullable<T extends BaseNodeData>(id: NodeId | undefined): T | undefined {
  const { graph } = useFlowContext();
  return useSyncExternalStore(
    (f) => (id ? graph.store.addNodeListener(id, f) : () => {}),
    () => (id ? graph.store.getNodeDataNullable<T>(id) : undefined),
  );
}

/**
 * A *reactive* hook that returns the edges of the flow graph.
 *
 * @example
 * ```tsx
 * const edges = useEdges();
 * edges.filter((edge) => edge.source === 'node-id');
 * ```
 *
 * @returns The edges of the flow graph
 */
export function useEdges(): Edge[] {
  const { graph } = useFlowContext();
  const subscribe = useCallback((f: () => void) => graph.store.addEdgesListener(f), [graph.store]);
  return useSyncExternalStore(subscribe, () => graph.store.getEdges());
}

/**
 * A *non-reactive* hook that returns a function that returns the nodes of the flow graph.
 *
 * @example
 * ```tsx
 * const getNodes = useGetNodes();
 * getNodes().filter((node) => node.type === 'blurV2');
 * ```
 *
 * @returns A function that returns the nodes of the flow graph
 */
export function useGetNodes() {
  const { graph } = useFlowContext();
  return useCallback(() => graph.store.getNodes(), [graph]);
}

/**
 * A *non-reactive* hook that returns a function that returns the edges of the flow graph.
 *
 * @example
 * ```tsx
 * const getEdges = useGetEdges();
 * getEdges().filter((edge) => edge.source === 'node-id');
 * ```
 *
 * @returns A function that returns the edges of the flow graph
 */
export function useGetEdges() {
  const { graph } = useFlowContext();
  return useCallback(() => graph.store.getEdges(), [graph]);
}

export function useNodeRenderTarget(id: NodeId, index: number = 0) {
  const { graph } = useFlowContext();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const onWheel = (e: WheelEvent) => {
      graph.wasm.call((wasm) => {
        wasm.onWheel(e);
      });
    };

    const canvas = canvasRef.current;
    if (canvas) {
      canvas.addEventListener('wheel', onWheel);
    }
    graph.wasm.call((wasm) => {
      wasm.setNodeCanvas(id, index, canvas ?? undefined);
    });
    return () => {
      if (canvas) {
        canvas.removeEventListener('wheel', onWheel);
      }
      graph.wasm.call((wasm) => {
        wasm.setNodeCanvas(id, index, undefined);
      });
    };
  }, [graph, id, index]);

  return canvasRef;
}

export function useNodeValue(nodeId: NodeId, index: number = 0) {
  const { graph } = useFlowContext();
  const snapshot = useRef<EvaluatedValue>(undefined);
  const getSnapshot = useCallback(() => {
    const newValue = graph.evaluateNode(nodeId, index);
    if (!isEqual(newValue, snapshot.current)) {
      snapshot.current = newValue;
    }
    return snapshot.current;
  }, [graph, index, nodeId]);

  const subscribe = useCallback((f: () => void) => graph.addNodeInputListener(nodeId, f), [graph, nodeId]);
  return useSyncExternalStore(subscribe, getSnapshot);
}

export function useNodeStringValue(nodeId: NodeId, index: number = 0): string | undefined {
  const nodeValue = useNodeValue(nodeId, index);
  if (nodeValue && nodeValue.type !== 'string') {
    throw new Error(`unexpected value type "${nodeValue.type}" for node ${nodeId}`);
  }
  return nodeValue?.value;
}

export function useNodeStringArrayValue(nodeId: NodeId, index: number = 0): string[] {
  const nodeValue = useNodeValue(nodeId, index);
  if (nodeValue && nodeValue.type !== 'string_array') {
    throw new Error(`unexpected value type "${nodeValue.type}" for node ${nodeId}`);
  }
  return useMemo(() => nodeValue?.value ?? [], [nodeValue]);
}

function useNodeInputStore<T>(nodeId: NodeId, select: () => T): T {
  const { graph } = useFlowContext();
  const snapshotRef = useRef<T>(undefined as unknown as T);

  const getSnapshot = useCallback(() => {
    const next = select();
    if (!isEqual(next, snapshotRef.current)) snapshotRef.current = next;
    return snapshotRef.current;
  }, [select]);

  const subscribe = useCallback(
    (onChange: () => void) => graph.addNodeInputListener(nodeId, onChange),
    [graph, nodeId],
  );

  return useSyncExternalStore(subscribe, getSnapshot);
}

/** Calls `onChange` on any change to the dependencies of this node in the graph.
 * If the `immediate` option is set, it will be called before subscribing to changes.
 * Notes:
 * - ongoing changes do not trigger the callback (i.e. dragging a slider will trigger this only at the end of the drag)
 * - the callback will run even if the evaluated inputs haven't changed. this is intentional.
 * */
export function useOnNodeInputChange(nodeId: NodeId, onChange: () => void, options?: { immediate: boolean }) {
  const { graph } = useFlowContext();
  useEffect(() => {
    if (options?.immediate) onChange();
    return graph.addNodeInputListener(nodeId, onChange);
  }, [nodeId, graph, onChange, options?.immediate]);
}

/**
 * Reactive hook that returns the value of the given input for a node
 * Causes a component to rerender if upstream changes to the graph changes the input value
 * Will crash if given input id doesn't exist for the node.
 */
export function useNodeInput(nodeId: NodeId, inputId: InputId): EvaluatedValue | undefined {
  const { graph } = useFlowContext();
  const select = useCallback(() => graph.evaluateInput(nodeId, inputId), [graph, nodeId, inputId]);
  return useNodeInputStore(nodeId, select);
}

/**
 * Reactive hook that returns an map of with *all* inputs to the node
 * Causes a component to rerender if upstream changes to the graph changes any input value
 * Will crash if given input id doesn't exist for the node.
 */
export function useNodeInputs(nodeId: NodeId): Record<InputId, EvaluatedValue | undefined> {
  const { graph } = useFlowContext();
  const select = useCallback(
    () => Object.fromEntries(graph.getInputIds(nodeId).map((id) => [id, graph.evaluateInput(nodeId, id)])),
    [graph, nodeId],
  );
  return useNodeInputStore(nodeId, select);
}

export type InputChange =
  | { inputId: InputId; value: EvaluatedValue; changeType: 'added' | 'updated' }
  | { inputId: InputId; value: undefined; changeType: 'deleted' };

export function useOnInputsChange(nodeId: NodeId, onChange: (changes: InputChange[]) => void) {
  const { graph } = useFlowContext();
  const prevRef = useRef<Map<InputId, EvaluatedValue>>(new Map());

  useEffect(() => {
    prevRef.current = snapshotInputs(graph, nodeId);

    return graph.addNodeInputListener(nodeId, () => {
      const next = snapshotInputs(graph, nodeId);
      const changes = diffSnapshots(prevRef.current, next);
      prevRef.current = next;
      if (changes.length > 0) onChange(changes);
    });
  }, [graph, nodeId, onChange]);
}

function snapshotInputs(graph: FlowGraph, nodeId: NodeId): Map<InputId, EvaluatedValue> {
  const snapshot = new Map<InputId, EvaluatedValue>();
  for (const id of graph.getInputIds(nodeId)) {
    const value = graph.evaluateInput(nodeId, id);
    if (value !== undefined) {
      snapshot.set(id, value);
    }
  }
  return snapshot;
}

function diffSnapshots(prev: Map<InputId, EvaluatedValue>, next: Map<InputId, EvaluatedValue>): InputChange[] {
  const changes: InputChange[] = [];

  for (const [inputId, value] of next) {
    const prevValue = prev.get(inputId);
    if (prevValue === undefined) {
      changes.push({ inputId, value, changeType: 'added' });
    } else if (!isEqual(prevValue, value)) {
      changes.push({ inputId, value, changeType: 'updated' });
    }
  }

  for (const inputId of prev.keys()) {
    if (!next.has(inputId)) {
      changes.push({ inputId, value: undefined, changeType: 'deleted' });
    }
  }

  return changes;
}

export function useFlowView() {
  const { graph } = useFlowContext();

  return useMemo(() => new FlowView(graph), [graph]);
}

export function useCustomGroupView(groupNodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new CustomGroupView(graph, groupNodeId), [graph, groupNodeId]);
}

export function useVideoView(nodeId: NodeId, index: number = 0) {
  const { graph } = useFlowContext();
  return useMemo(() => new VideoView(graph, nodeId, index), [graph, nodeId, index]);
}

export function useCompositorView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new CompositorView(graph, nodeId), [graph, nodeId]);
}

export function useBlurView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new BlurView(graph, nodeId), [graph, nodeId]);
}

export function useComparisonView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new ComparisonView(graph, nodeId), [graph, nodeId]);
}

export function useBlendView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new BlendView(graph, nodeId), [graph, nodeId]);
}

export function useLevelsView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new LevelsView(graph, nodeId), [graph, nodeId]);
}

export function useDilationErosionView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new DilationErosionView(graph, nodeId), [graph, nodeId]);
}

export function useChannelsView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new ChannelsView(graph, nodeId), [graph, nodeId]);
}

export function useTextView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new TextView(graph, nodeId), [graph, nodeId]);
}

export function usePromptView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new PromptView(graph, nodeId), [graph, nodeId]);
}

export function usePromptConcatView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new PromptConcatView(graph, nodeId), [graph, nodeId]);
}

export function useNumberView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new NumberView(graph, nodeId), [graph, nodeId]);
}

export function useThreeDView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new ThreeDView(graph, nodeId), [graph, nodeId]);
}

export function usePointCloudView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new PointCloudView(graph, nodeId), [graph, nodeId]);
}

export function useStringArrayView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new StringArrayView(graph, nodeId), [graph, nodeId]);
}

export function useImportView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new ImportView(graph, nodeId), [graph, nodeId]);
}

export function useMediaIteratorView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new MediaIteratorView(graph, nodeId), [graph, nodeId]);
}

export function useModelBaseView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new ModelBaseView(graph, nodeId), [graph, nodeId]);
}

export function useParameterView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new ParameterView(graph, nodeId), [graph, nodeId]);
}

export function useCropView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new CropView(graph, nodeId), [graph, nodeId]);
}

export function useResizeView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new ResizeView(graph, nodeId), [graph, nodeId]);
}

export function useListSelectorView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new ListSelectorView(graph, nodeId), [graph, nodeId]);
}

export function usePainterView(id: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new PainterView(graph, id), [graph, id]);
}

export function useNumberSelectorView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new NumberSelectorView(graph, nodeId), [graph, nodeId]);
}

export function useExportView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new ExportView(graph, nodeId), [graph, nodeId]);
}

export function usePreviewView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new PreviewView(graph, nodeId), [graph, nodeId]);
}

export function useKlingElementView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new KlingElementView(graph, nodeId), [graph, nodeId]);
}

export function useToggleView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new ToggleView(graph, nodeId), [graph, nodeId]);
}

export function useSeedView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new SeedView(graph, nodeId), [graph, nodeId]);
}

export function useLoraView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new LoraView(graph, nodeId), [graph, nodeId]);
}

export function useMaskExtractorView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new MaskExtractorView(graph, nodeId), [graph, nodeId]);
}

export function useGroupsManagerView() {
  const { graph } = useFlowContext();
  return useMemo(() => new GroupsManagerView(graph), [graph]);
}

export function useNodeAssetManager() {
  const { graph } = useFlowContext();
  return useMemo(() => new NodeAssetManager(graph), [graph]);
}

export function useImportModelView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new ImportModelView(graph, nodeId), [graph, nodeId]);
}

export function useStickyNoteView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new StickyNoteView(graph, nodeId), [graph, nodeId]);
}

export function useFigmaCompositorView(nodeId: NodeId) {
  const { graph } = useFlowContext();
  return useMemo(() => new FigmaCompositorView(graph, nodeId), [graph, nodeId]);
}

type FlowProviderProps = React.PropsWithChildren<{
  nodes: Node[];
  edges: Edge[];
  workflowCustomFontsUrlsMap: ReadonlyMap<string, string>;
}>;
export function FlowProvider({
  nodes: initialNodes,
  edges: initialEdges,
  workflowCustomFontsUrlsMap,
  children,
}: FlowProviderProps) {
  const { canvasRef } = useCanvas();
  const setLoadingState = useWorkflowStore((state) => state.setLoadingState);
  const role = useWorkflowStore((state) => state.workflowRole);
  const { t } = useTranslation();

  const [graph, setGraph] = useState<FlowGraph | undefined>();
  const [errorType, setErrorType] = useState<FlowErrorType | undefined>(undefined);
  const [warningType, setWarningType] = useState<FlowWarningType | undefined>(undefined);
  const closeEditor = useEditorStore((selector) => selector.onClose);

  useEffect(() => {
    let newGraph: FlowGraph | undefined;
    const initGraph = () => {
      setErrorType(undefined);
      setLoadingState('flow-graph', 'loading');
      // FlowGraph constructor is a long and synchronous operation, so we need to set the loading state before and after
      newGraph = new FlowGraph(
        initialNodes,
        initialEdges,
        canvasRef.current,
        role === 'editor',
        setErrorType,
        setWarningType,
        workflowCustomFontsUrlsMap,
      );
      setGraph(newGraph);
      setLoadingState('flow-graph', 'loaded');
    };
    initGraph();
    return () => {
      newGraph?.dispose();
      setGraph(undefined);
    };
  }, [canvasRef, initialEdges, initialNodes, setLoadingState, role, workflowCustomFontsUrlsMap]);

  const updateSnackbarData = useGlobalStore((state) => state.updateSnackbarData);
  useLayoutEffect(() => {
    if (errorType === 'recovered') {
      closeEditor();
      updateSnackbarData({
        text: t(I18N_KEYS.ERROR_PAGE.RECOVERED_FROM_ERROR),
        icon: null,
        isOpen: true,
        duration: 5000,
        severity: 'error',
      });
      setErrorType(undefined);
    }
  }, [errorType, updateSnackbarData, t, closeEditor]);

  useEffect(() => {
    let text = '';
    switch (warningType) {
      case 'failed-deserialize-3d-model':
        text = t(I18N_KEYS.FLOW_WARNINGS.FAILED_DESERIALIZE_3D_MODEL);
        break;
      case 'failed-deserialize-font':
        text = t(I18N_KEYS.FLOW_WARNINGS.FAILED_DESERIALIZE_FONT);
        break;
      case 'failed-deserialize-image':
        text = t(I18N_KEYS.FLOW_WARNINGS.FAILED_DESERIALIZE_IMAGE);
        break;
      case 'failed-deserialize-video':
        text = t(I18N_KEYS.FLOW_WARNINGS.FAILED_DESERIALIZE_VIDEO);
        break;
      case 'failed-download-image':
        text = t(I18N_KEYS.FLOW_WARNINGS.FAILED_DOWNLOAD_IMAGE);
        break;
      case 'failed-download-video':
        text = t(I18N_KEYS.FLOW_WARNINGS.FAILED_DOWNLOAD_VIDEO);
        break;
      case 'failed-download-font':
        text = t(I18N_KEYS.FLOW_WARNINGS.FAILED_DOWNLOAD_FONT);
        break;
      case 'failed-download-3d-model':
        text = t(I18N_KEYS.FLOW_WARNINGS.FAILED_DOWNLOAD_3D_MODEL);
        break;
      default:
        return;
    }

    updateSnackbarData({
      text,
      icon: null,
      isOpen: true,
      duration: 5000,
      severity: 'warning',
    });
    setWarningType(undefined);
  }, [warningType, updateSnackbarData, t]);

  switch (errorType) {
    case 'fatal':
      return <ErrorPage />;
    case 'webgl-not-supported':
      return <WebGLNotSupportedPage />;
    case 'context-lost':
      return <GPUDriverErrorPage />;
    case 'recovered':
      return null; // Need to rerender the entire React tree
    default:
  }

  if (!graph) {
    return null;
  }

  return <FlowContext.Provider value={{ graph }}>{children}</FlowContext.Provider>;
}
